import { useState } from "react"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import {
  useInviteMember,
  useRemoveMember,
  useResendInvite,
  useRevokeInvite,
  useTeamInvites,
  useTeamMembers,
  useUpdateMemberRole,
} from "@/lib/api/use-teams"
import type { ApiTeamMemberInvite, TeamMemberSummary, TeamType } from "@/lib/api/types"
import { useTeamPermissions } from "@/lib/team/use-team-permissions"
import { TeamMembersTable } from "./team-members-table"
import { InviteMemberDialog } from "./invite-member-dialog"
import { RemoveMemberDialog } from "./remove-member-dialog"
import { ChangeRoleDialog } from "./change-role-dialog"

// Settings → {entity} → Members & invites. Wraps TeamMembersTable scoped to
// a specific teamId; each entity's settings page mounts its own panel so
// every team gets a fresh, scoped fetch. Owns three dialogs:
//
//   - InviteMemberDialog        → POST   /v1/teams/:id/invites
//   - ChangeRoleDialog          → PATCH  /v1/teams/:id/members/:userId  (#49 shipped)
//   - RemoveMemberDialog        → DELETE /v1/teams/:id/members/:userId  (#48 shipped)
//
// Resend uses the dedicated /resend endpoint (#50 shipped) — we no longer
// re-POST the invite to "duplicate-detect" our way to a resend.

interface TeamMembersPanelProps {
  teamId: number | string
  teamName: string
  teamKind: "group" | "dealership" | "bodyshop"
}

const TEAM_TYPE_BY_KIND: Record<TeamMembersPanelProps["teamKind"], TeamType> = {
  group: "DealershipGroup",
  dealership: "Dealership",
  bodyshop: "Bodyshop",
}

function memberDisplayName(m: TeamMemberSummary) {
  const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim()
  return name || m.email
}

export function TeamMembersPanel({
  teamId,
  teamName,
  teamKind,
}: TeamMembersPanelProps) {
  const numericTeamId = Number(teamId)
  const teamType = TEAM_TYPE_BY_KIND[teamKind]
  const perms = useTeamPermissions(numericTeamId)

  const resendMutation = useResendInvite(numericTeamId)
  const revokeMutation = useRevokeInvite(numericTeamId)
  const removeMutation = useRemoveMember(numericTeamId)
  const updateRoleMutation = useUpdateMemberRole(numericTeamId)

  const { data: members = [], isLoading: membersLoading } = useTeamMembers(numericTeamId)
  const { data: pendingInvites = [], error: invitesError } = useTeamInvites(numericTeamId)
  const invitesUnavailable = (invitesError as ApiError | undefined)?.status === 404

  const [inviteOpen, setInviteOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<TeamMemberSummary | null>(null)
  const [memberToChangeRole, setMemberToChangeRole] = useState<TeamMemberSummary | null>(null)

  const handleResend = async (invite: ApiTeamMemberInvite) => {
    try {
      await resendMutation.mutateAsync(invite.uuid)
      toast.success(`Invite resent to ${invite.user.email}`)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't resend invite"
      toast.error(msg)
    }
  }

  const handleRevoke = async (invite: ApiTeamMemberInvite) => {
    try {
      await revokeMutation.mutateAsync(invite.uuid)
      toast.success(`Invite for ${invite.user.email} revoked`)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't revoke invite"
      toast.error(msg)
    }
  }

  const handleRemoveConfirm = async () => {
    if (!memberToRemove) return
    const target = memberToRemove
    try {
      await removeMutation.mutateAsync(target.id)
      toast.success(`${memberDisplayName(target)} removed from ${teamName}`)
      setMemberToRemove(null)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't remove member"
      toast.error(msg)
    }
  }

  const handleChangeRoleConfirm = async (newRole: string) => {
    if (!memberToChangeRole) return
    const target = memberToChangeRole
    try {
      await updateRoleMutation.mutateAsync({
        userId: target.id,
        data: { role: newRole },
      })
      toast.success(`${memberDisplayName(target)} is now ${newRole}`)
      setMemberToChangeRole(null)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't change role"
      toast.error(msg)
    }
  }

  return (
    <div>
      <TeamMembersTable
        teamId={numericTeamId}
        members={members}
        pendingInvites={pendingInvites}
        isLoading={membersLoading}
        invitesUnavailable={invitesUnavailable}
        onInvite={perms.canInvite ? () => setInviteOpen(true) : undefined}
        onResendInvite={handleResend}
        onRevokeInvite={handleRevoke}
        onChangeRole={
          perms.canManageMembers ? (m) => setMemberToChangeRole(m) : undefined
        }
        onRemoveMember={
          perms.canManageMembers ? (m) => setMemberToRemove(m) : undefined
        }
      />

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        teamId={numericTeamId}
        teamName={teamName}
        teamType={teamType}
      />

      <RemoveMemberDialog
        member={memberToRemove}
        teamName={teamName}
        loading={removeMutation.isPending}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemoveConfirm}
      />

      <ChangeRoleDialog
        member={memberToChangeRole}
        teamName={teamName}
        teamType={teamType}
        loading={updateRoleMutation.isPending}
        onClose={() => setMemberToChangeRole(null)}
        onConfirm={handleChangeRoleConfirm}
      />
    </div>
  )
}
